import {
  Alert,
  BarChart,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Grid,
  Header,
  Input,
  KeyValuePairs,
  LineChart,
  Link,
  PieChart,
  SegmentedControl,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
  Toggle,
} from '@cloudscape-design/components';
import type { MixedLineBarChartProps } from '@cloudscape-design/components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  getPublicScorecardTracking,
  getPublicScorecardTrackingSeries,
  getPublicScorecardTrackingSnapshot,
  projectPublicScorecardRating,
  type ScorecardTrackingMetric,
} from '../../common/api/scorecardTracking';
import { LoadingOrError } from '../../components/Loading';
import ReportCardHeader from '../../components/ReportCardHeader';
import { ScorecardGrade, ScorecardIdentity } from '../../components/ScorecardIdentity';
import { SourceRatingsSummary } from '../../components/SourceRatingsSummary';
import { StarsIcon } from '../../components/StarsIcon';
import { StatisticBox } from '../../components/StatisticBox';
import { ViewLayout } from '../../components/ViewLayout';
import {
  formatScorecardAxisPercentile,
  formatScorecardPercentile,
} from '../../common/scorecardTracking';
import { ScorecardSourceLabel, scorecardSourceName } from '../../common/scorecardSources';
import type { StatisticStatus } from '../../common/types';
import type { ScorecardHistorySearch } from '../../routes/h.$slug';
import { getRatingTrend } from '../../common/scorecardInsights';

const METRIC_OPTIONS = [
  { value: 'overall_score', label: 'Overall score percentile' },
  { value: 'average_rating', label: 'Average review rating' },
  { value: 'review_count', label: 'Review volume' },
  { value: 'response_rate', label: 'Response rate' },
  { value: 'average_response_time', label: 'Average response time' },
  { value: 'platform_rating', label: 'Platform overall rating' },
];

const WINDOW_OPTIONS = [
  { id: '30', text: '30 days' },
  { id: '60', text: '60 days' },
  { id: '90', text: '90 days' },
  { id: '180', text: '180 days' },
];

function titleCase(source: string): string {
  return scorecardSourceName(source);
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatMetricValue(metric: string, value?: number | null): string {
  if (value == null) return 'Not available';
  if (metric === 'response_rate') return `${Math.round(value * 100)}%`;
  if (metric === 'average_response_time') return `${value.toFixed(1)} days`;
  if (metric === 'review_count') return Math.round(value).toLocaleString();
  if (metric === 'overall_score') return `${formatScorecardPercentile(value)} percentile`;
  return value.toFixed(2);
}

function formatChartMetricValue(metric: string, value: number): string {
  if (metric === 'overall_score') return formatScorecardAxisPercentile(value);
  return formatMetricValue(metric, value);
}

function formatMetricDelta(metric: string, value: number): string {
  if (metric === 'overall_score') {
    const percentilePoints = Math.abs(value) <= 1 ? value * 100 : value;
    return `${percentilePoints.toFixed(1)} percentile points`;
  }
  return formatMetricValue(metric, value);
}

function normalizePercentile(value?: number | null): number {
  if (value == null) return 0;
  return Math.abs(value) > 1 ? value / 100 : value;
}

function statisticStatus(value?: string | null): StatisticStatus {
  if (
    value === 'extremely-low' ||
    value === 'below-average' ||
    value === 'average' ||
    value === 'above-average'
  ) {
    return value;
  }
  return 'average';
}

function formatResponseTime(value?: number | null): string {
  if (value == null) return '—';
  return value < 1 ? '< 24 hours' : `${value.toFixed(1)} days`;
}

function starData(metric?: ScorecardTrackingMetric) {
  if (!metric) return [];
  const values = [
    { title: '5 stars', value: metric.five_star_count, color: '#037f0c' },
    { title: '4 stars', value: metric.four_star_count, color: '#65a30d' },
    { title: '3 stars', value: metric.three_star_count, color: '#f59e0b' },
    { title: '2 stars', value: metric.two_star_count, color: '#ea580c' },
    { title: '1 star', value: metric.one_star_count, color: '#d13212' },
  ];
  if (metric.unknown_star_count > 0) {
    values.push({ title: 'Unknown', value: metric.unknown_star_count, color: '#5f6b7a' });
  }
  return values;
}

export default function ScorecardHistoryPage() {
  const { slug } = useParams({ from: '/h/$slug' });
  const search = useSearch({ from: '/h/$slug' });
  const navigate = useNavigate({ from: '/h/$slug' });
  const source = search.source ?? 'google';
  const windowDays = search.window ?? 180;
  const metric = search.metric ?? 'overall_score';
  const horizon = search.horizon ?? 12;
  const includeReconstructed = search.reconstructed ?? true;
  const selectedSnapshotUuid = search.snapshot ?? null;
  const [projectionMode, setProjectionMode] = useState('increase');
  const [ratingGoal, setRatingGoal] = useState('0.1');
  const [incomingRating, setIncomingRating] = useState('5');

  const updateSearch = useCallback(
    (patch: Partial<ScorecardHistorySearch>) => {
      navigate({
        replace: true,
        search: (previous) => ({ ...previous, ...patch }),
      });
    },
    [navigate],
  );

  useEffect(() => {
    const existing = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const meta = existing ?? document.createElement('meta');
    const priorContent = existing?.content;
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    if (!existing) document.head.append(meta);
    return () => {
      if (!existing) meta.remove();
      else meta.content = priorContent ?? '';
    };
  }, []);

  const historyQuery = useQuery({
    queryKey: ['scorecard-history', slug],
    queryFn: () => getPublicScorecardTracking(slug),
    staleTime: 5 * 60_000,
    refetchInterval: (query) => (query.state.data?.is_updating ? 4_000 : false),
  });

  useEffect(() => {
    if (
      historyQuery.data?.source_availability.length &&
      source !== 'all' &&
      !historyQuery.data.source_availability.includes(source)
    ) {
      updateSearch({ source: historyQuery.data.source_availability[0] });
    }
  }, [historyQuery.data?.source_availability, source, updateSearch]);

  const seriesQuery = useQuery({
    queryKey: [
      'scorecard-history-series',
      slug,
      metric,
      source,
      windowDays,
      horizon,
      includeReconstructed,
    ],
    queryFn: () =>
      getPublicScorecardTrackingSeries({
        slug,
        metric,
        source,
        windowDays,
        months: horizon,
        includeReconstructed,
      }),
    enabled: Boolean(historyQuery.data),
    refetchInterval: historyQuery.data?.is_updating ? 4_000 : false,
  });

  const snapshotQuery = useQuery({
    queryKey: ['scorecard-history-snapshot', slug, selectedSnapshotUuid],
    queryFn: () => getPublicScorecardTrackingSnapshot(slug, selectedSnapshotUuid ?? ''),
    enabled: Boolean(selectedSnapshotUuid),
  });

  const projectionMutation = useMutation({
    mutationFn: () => {
      const goal = Number.parseFloat(ratingGoal);
      const incoming = Number.parseFloat(incomingRating);
      return projectPublicScorecardRating(slug, {
        source,
        incoming_rating: incoming,
        target_rating: projectionMode === 'target' ? goal : null,
        increase_by: projectionMode === 'increase' ? goal : null,
      });
    },
  });

  const selectedMetricOption =
    METRIC_OPTIONS.find((option) => option.value === metric) ?? METRIC_OPTIONS[0];
  const lineSeries = useMemo(() => {
    const points = (seriesQuery.data?.points ?? []).filter((point) => point.value != null);
    const sources = [...new Set(points.map((point) => point.source))];
    return sources.map((seriesSource) => ({
      type: 'line' as const,
      title:
        source === 'all'
          ? titleCase(seriesSource)
          : (selectedMetricOption.label ?? 'Scorecard measure'),
      data: points
        .filter((point) => point.source === seriesSource)
        .map((point) => ({ x: new Date(point.as_of), y: point.value ?? 0 })),
      valueFormatter: (value: number) => formatMetricValue(metric, value),
    }));
  }, [metric, selectedMetricOption.label, seriesQuery.data?.points, source]);
  const [visibleLineSeries, setVisibleLineSeries] =
    useState<ReadonlyArray<MixedLineBarChartProps.ChartSeries<Date>>>(lineSeries);
  useEffect(() => {
    setVisibleLineSeries(lineSeries);
  }, [lineSeries]);
  const drilldownSnapshot = snapshotQuery.data ?? historyQuery.data?.latest_snapshot;
  const currentMetric = drilldownSnapshot?.metrics?.find(
    (item) => item.source === source && item.window_days === windowDays,
  );
  const distribution = starData(currentMetric);
  const availableMetricOptions = METRIC_OPTIONS;
  const comparablePoints =
    source === 'all'
      ? []
      : (seriesQuery.data?.points ?? []).filter((point) => point.source === source);
  const latestPoint = [...comparablePoints].reverse().find((point) => point.value != null);
  const priorPoint = [...comparablePoints].reverse().filter((point) => point.value != null)[1];
  const pointDelta =
    latestPoint?.value != null && priorPoint?.value != null
      ? latestPoint.value - priorPoint.value
      : null;

  if (historyQuery.isLoading || !historyQuery.data || historyQuery.error) {
    return (
      <ViewLayout
        header={<ReportCardHeader />}
        contentType="dashboard"
        navigationHide
        headerVariant="high-contrast"
      >
        <LoadingOrError
          loading={historyQuery.isLoading}
          error={historyQuery.error}
          retry={historyQuery.refetch}
        />
      </ViewLayout>
    );
  }

  const history = historyQuery.data;
  const historySourceOptions = history.source_options ?? [];
  const availableSourceOptions = historySourceOptions.filter((item) => item.has_metrics);
  const lifetimeSourceOptions = availableSourceOptions.filter(
    (item) => item.lifetime_rating != null,
  );
  const sourceSelectOptions = [
    { value: 'all', label: 'All available sources', description: 'Compare one line per source' },
    ...availableSourceOptions.map((item) => ({
      value: item.source,
      label: titleCase(item.source),
      description: item.reason ?? undefined,
    })),
  ];
  const selectedSourceOption = sourceSelectOptions.find((item) => item.value === source) ?? {
    value: 'all',
    label: 'All available sources',
  };
  const selectedSourceAvailability = availableSourceOptions.find((item) => item.source === source);
  const identitySource = source === 'all' ? 'google' : source;
  const identitySourceAvailability = availableSourceOptions.find(
    (item) => item.source === identitySource,
  );
  const identityMetric = drilldownSnapshot?.metrics?.find(
    (item) => item.source === identitySource && item.window_days === windowDays,
  );
  const availableHistoryPoints = (seriesQuery.data?.points ?? []).filter(
    (point) => point.value != null,
  );
  const hiddenHistoryPointCount =
    (seriesQuery.data?.points.length ?? 0) - availableHistoryPoints.length;

  return (
    <ViewLayout
      header={<ReportCardHeader />}
      contentType="dashboard"
      navigationHide
      headerVariant="high-contrast"
    >
      <SpaceBetween size="l">
        <Container
          header={
            <Header
              variant="h1"
              description={`Updated ${formatDate(history.updated_at)}`}
              actions={
                history.latest_scorecard_slug ? (
                  <Button href={`/${history.latest_scorecard_slug}`} target="_blank">
                    Open latest point-in-time scorecard
                  </Button>
                ) : undefined
              }
            >
              Scorecard History
            </Header>
          }
        >
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-stretch gap-4">
            <div className="flex min-w-0 flex-col gap-4">
              <ScorecardIdentity
                name={history.place.name}
                address={history.place.address}
                grade={identityMetric?.composite_grade}
                percentile={identityMetric?.composite_percentile}
                rating={
                  identitySourceAvailability?.lifetime_rating ??
                  (identitySource === 'google' ? history.place.rating : null)
                }
                reviewCount={
                  identitySourceAvailability?.lifetime_review_count ??
                  (identitySource === 'google' ? history.place.total_review_count : null)
                }
                reviewLink={identitySourceAvailability?.source_link}
                sourceName={titleCase(identitySource)}
                phoneNumber={history.place.phone_number}
              />
              <SegmentedControl
                selectedId={String(windowDays)}
                label="View past"
                options={WINDOW_OPTIONS}
                onChange={({ detail }) =>
                  updateSearch({ window: Number(detail.selectedId), snapshot: undefined })
                }
              />
            </div>
            <SourceRatingsSummary
              items={lifetimeSourceOptions.map((item) => ({
                source: item.source,
                rating: item.lifetime_rating,
                reviewCount: item.lifetime_review_count,
                link: item.source_link,
              }))}
              onSourceChange={(nextSource) =>
                updateSearch({ source: nextSource, snapshot: undefined })
              }
            />
          </div>
        </Container>

        {history.is_updating ? (
          <Alert type="info" header="Scorecard History is being updated">
            The current page remains usable while the latest reviews and scorecard points are
            recalculated. New values will appear automatically.
          </Alert>
        ) : identityMetric?.composite_grade == null ? (
          <Alert type="error" header="This Scorecard History needs attention">
            The current source data exists, but the scorecard score was not materialized. The
            internal Watchlist view can rebuild the retained history without another source crawl.
          </Alert>
        ) : null}

        {identityMetric ? (
          <SpaceBetween size="m">
            <Header variant="h2">
              Past {windowDays} days · {titleCase(identitySource)}
            </Header>
            <Grid
              gridDefinition={[
                { colspan: { default: 12, xs: 3 } },
                { colspan: { default: 12, xs: 3 } },
                { colspan: { default: 12, xs: 3 } },
                { colspan: { default: 12, xs: 3 } },
              ]}
            >
              <StatisticBox
                statistic={identityMetric.review_count.toLocaleString()}
                percentile={normalizePercentile(identityMetric.review_count_percentile)}
                status={statisticStatus(identityMetric.review_count_status)}
                details={`${((identityMetric.review_count / identityMetric.window_days) * 30).toFixed(0)} / month`}
                icon="contact"
                label="Reviews"
              />
              <StatisticBox
                statistic={
                  identityMetric.review_count === 0
                    ? 'N/A'
                    : identityMetric.average_rating.toFixed(2)
                }
                percentile={normalizePercentile(identityMetric.average_rating_percentile)}
                status={statisticStatus(identityMetric.average_rating_status)}
                icon="star"
                label="Average Rating"
                trend={
                  identityMetric.review_count === 0
                    ? null
                    : getRatingTrend(
                        identityMetric.average_rating,
                        identityMetric.lifetime_rating,
                      )
                }
              />
              {identityMetric.review_count === 0 || identityMetric.response_rate != null ? (
                <StatisticBox
                  statistic={
                    identityMetric.review_count === 0
                      ? 'N/A'
                      : formatMetricValue('response_rate', identityMetric.response_rate)
                  }
                  percentile={normalizePercentile(identityMetric.response_rate_percentile)}
                  status={statisticStatus(identityMetric.response_rate_status)}
                  icon="undo"
                  label="Your Response Rate"
                />
              ) : null}
              {identityMetric.review_count === 0 ||
              identityMetric.average_response_time_days != null ? (
                <StatisticBox
                  statistic={
                    identityMetric.review_count === 0
                      ? 'N/A'
                      : formatResponseTime(identityMetric.average_response_time_days)
                  }
                  percentile={normalizePercentile(identityMetric.average_response_time_percentile)}
                  status={statisticStatus(identityMetric.average_response_time_status)}
                  icon="calendar"
                  label="Avg Response Time"
                />
              ) : null}
            </Grid>
          </SpaceBetween>
        ) : null}

        <Container
          header={
            <Header
              variant="h2"
              description="Change the source, rolling period, and scorecard component to explore the business over time."
            >
              Scorecard over time
            </Header>
          }
        >
          <SpaceBetween size="l">
            <Grid
              gridDefinition={[
                { colspan: { default: 12, s: 3 } },
                { colspan: { default: 12, s: 3 } },
                { colspan: { default: 12, s: 3 } },
                { colspan: { default: 12, s: 3 } },
              ]}
            >
              <FormField label="Review source">
                <Select
                  selectedOption={selectedSourceOption}
                  options={sourceSelectOptions}
                  onChange={({ detail }) =>
                    updateSearch({
                      source: detail.selectedOption.value ?? 'google',
                      snapshot: undefined,
                    })
                  }
                />
              </FormField>
              <FormField label="Scorecard measure">
                <Select
                  selectedOption={selectedMetricOption}
                  options={availableMetricOptions}
                  onChange={({ detail }) =>
                    updateSearch({ metric: detail.selectedOption.value ?? 'overall_score' })
                  }
                />
              </FormField>
              <FormField label="Rolling comparison period">
                <Select
                  selectedOption={{
                    value: String(windowDays),
                    label:
                      WINDOW_OPTIONS.find((option) => option.id === String(windowDays))?.text ??
                      `${windowDays} days`,
                  }}
                  options={WINDOW_OPTIONS.map((option) => ({
                    value: option.id,
                    label: option.text,
                  }))}
                  onChange={({ detail }) =>
                    updateSearch({
                      window: Number(detail.selectedOption.value ?? 180),
                      snapshot: undefined,
                    })
                  }
                />
              </FormField>
              <FormField label="History horizon">
                <Select
                  selectedOption={
                    [
                      { value: '6', label: '6 months' },
                      { value: '12', label: '12 months' },
                      { value: '120', label: 'All available history' },
                    ].find((item) => item.value === String(horizon)) ?? null
                  }
                  options={[
                    { value: '6', label: '6 months' },
                    { value: '12', label: '12 months' },
                    { value: '120', label: 'All available history' },
                  ]}
                  onChange={({ detail }) =>
                    updateSearch({ horizon: Number(detail.selectedOption.value ?? 12) })
                  }
                />
              </FormField>
            </Grid>
            <Toggle
              checked={includeReconstructed}
              onChange={({ detail }) =>
                updateSearch({ reconstructed: detail.checked ? undefined : false })
              }
            >
              Include reconstructed monthly points from the initial 545-day backfill
            </Toggle>

            <ColumnLayout columns={3} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Latest value</Box>
                <Box fontSize="heading-xl" fontWeight="bold">
                  {formatMetricValue(metric, latestPoint?.value)}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Change from prior point</Box>
                <Box
                  fontSize="heading-xl"
                  fontWeight="bold"
                  color={
                    pointDelta == null
                      ? 'text-body-secondary'
                      : pointDelta >= 0
                        ? 'text-status-success'
                        : 'text-status-error'
                  }
                >
                  {pointDelta == null
                    ? source === 'all'
                      ? 'Compare source lines'
                      : 'Not available'
                    : `${pointDelta >= 0 ? '+' : ''}${formatMetricDelta(metric, pointDelta)}`}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Window and source</Box>
                <Box fontSize="heading-xl" fontWeight="bold">
                  {windowDays} days · {source === 'all' ? 'All sources' : titleCase(source)}
                </Box>
              </div>
            </ColumnLayout>

            {seriesQuery.isLoading || seriesQuery.error || availableHistoryPoints.length > 0 ? (
              <LineChart
                height={360}
                statusType={
                  seriesQuery.isLoading ? 'loading' : seriesQuery.error ? 'error' : 'finished'
                }
                series={lineSeries}
                visibleSeries={visibleLineSeries}
                onFilterChange={({ detail }) => setVisibleLineSeries(detail.visibleSeries)}
                xScaleType="time"
                xTitle="Snapshot month"
                yTitle={selectedMetricOption.label ?? 'Value'}
                xTickFormatter={(value) =>
                  value.toLocaleDateString(undefined, { month: 'short', year: '2-digit' })
                }
                yTickFormatter={(value) => formatChartMetricValue(metric, value)}
                detailPopoverFooter={(date) => {
                  const point = seriesQuery.data?.points.find(
                    (candidate) => new Date(candidate.as_of).getTime() === date.getTime(),
                  );
                  return point ? (
                    <Button
                      variant="inline-link"
                      onClick={() => updateSearch({ snapshot: point.snapshot_uuid })}
                    >
                      Inspect this scorecard point
                    </Button>
                  ) : null;
                }}
                empty={<Box textAlign="center">No scorecard points are available yet.</Box>}
                errorText="The chart could not be loaded."
                recoveryText="Retry"
                onRecoveryClick={() => seriesQuery.refetch()}
              />
            ) : (
              <Alert
                type={history.is_updating ? 'info' : 'error'}
                header={
                  history.is_updating
                    ? 'This scorecard series is still being calculated'
                    : 'This scorecard series could not be calculated'
                }
              >
                {history.is_updating
                  ? 'The page will refresh automatically when usable scorecard points are ready.'
                  : 'Choose another source or measure, or use the internal Watchlist view to rebuild the retained history.'}
              </Alert>
            )}

            <Table
              variant="embedded"
              header={<Header variant="h3">Accessible history data</Header>}
              items={availableHistoryPoints}
              wrapLines
              empty={<Box textAlign="center">No calculated history points.</Box>}
              columnDefinitions={[
                {
                  id: 'history-point',
                  header: 'Scorecard history point',
                  cell: (item) => (
                    <SpaceBetween size="xs">
                      <SpaceBetween size="xxxs">
                        <Link onFollow={() => updateSearch({ snapshot: item.snapshot_uuid })}>
                          {formatDate(item.as_of)}
                        </Link>
                        <StatusIndicator type={item.provenance === 'observed' ? 'success' : 'info'}>
                          {item.provenance === 'observed'
                            ? 'Observed monthly scorecard'
                            : 'Reconstructed from backfill'}
                        </StatusIndicator>
                      </SpaceBetween>
                      <div className="flex flex-wrap items-center gap-3">
                        {metric === 'overall_score' ? (
                          <ScorecardGrade grade={item.grade} percentile={item.value} compact />
                        ) : null}
                        <SpaceBetween size="xxxs">
                          <Box color="text-label">
                            {selectedMetricOption.label ?? 'Scorecard result'}
                          </Box>
                          <Box fontSize="heading-l" fontWeight="bold">
                            {formatMetricValue(metric, item.value)}
                          </Box>
                          {['average_rating', 'platform_rating'].includes(metric) &&
                          item.value != null ? (
                            <StarsIcon stars={item.value} />
                          ) : null}
                        </SpaceBetween>
                      </div>
                      <SpaceBetween size="xxxs">
                        <ScorecardSourceLabel source={item.source} />
                        <Box color="text-body-secondary">{item.window_days}-day rolling window</Box>
                        <StatusIndicator
                          type={item.coverage_state === 'complete' ? 'success' : 'warning'}
                        >
                          {item.coverage_state === 'complete'
                            ? 'Complete window'
                            : 'Partial window'}
                        </StatusIndicator>
                      </SpaceBetween>
                    </SpaceBetween>
                  ),
                },
              ]}
            />
            {hiddenHistoryPointCount > 0 ? (
              <Box color="text-body-secondary">
                {hiddenHistoryPointCount.toLocaleString()} incomplete history{' '}
                {hiddenHistoryPointCount === 1 ? 'point is' : 'points are'} hidden until a scorecard
                value can be calculated.
              </Box>
            ) : null}

            {selectedSnapshotUuid ? (
              <Container
                header={
                  <Header
                    variant="h3"
                    actions={
                      <Button
                        variant="inline-link"
                        onClick={() => updateSearch({ snapshot: undefined })}
                      >
                        Close drilldown
                      </Button>
                    }
                  >
                    {snapshotQuery.data
                      ? `Selected scorecard point · ${formatDate(snapshotQuery.data.as_of)}`
                      : 'Selected scorecard point'}
                  </Header>
                }
              >
                {snapshotQuery.isLoading || snapshotQuery.error || !snapshotQuery.data ? (
                  <LoadingOrError loading={snapshotQuery.isLoading} error={snapshotQuery.error} />
                ) : (
                  <SpaceBetween size="m">
                    <Alert
                      type={snapshotQuery.data.provenance === 'observed' ? 'success' : 'info'}
                      header={
                        snapshotQuery.data.provenance === 'observed'
                          ? 'Observed monthly snapshot'
                          : 'Reconstructed from the initial review backfill'
                      }
                    >
                      Coverage: {snapshotQuery.data.coverage_state}. Scoring version:{' '}
                      {snapshotQuery.data.scoring_version}.
                    </Alert>
                    <Table
                      variant="embedded"
                      items={snapshotQuery.data.metrics ?? []}
                      wrapLines
                      columnDefinitions={[
                        {
                          id: 'slice',
                          header: 'Scorecard slice',
                          minWidth: 190,
                          cell: (item) => (
                            <SpaceBetween size="xxxs">
                              <ScorecardSourceLabel source={item.source} />
                              <Box color="text-body-secondary">Past {item.window_days} days</Box>
                            </SpaceBetween>
                          ),
                        },
                        {
                          id: 'score',
                          header: 'Scorecard result',
                          minWidth: 300,
                          cell: (item) => (
                            <div className="flex flex-wrap items-center gap-3">
                              <ScorecardGrade
                                grade={item.composite_grade}
                                percentile={item.composite_percentile}
                                compact
                              />
                              <SpaceBetween size="xxxs">
                                <Box fontWeight="bold">
                                  {item.composite_percentile == null
                                    ? 'Score needs rebuilding'
                                    : `${formatScorecardPercentile(item.composite_percentile)} percentile`}
                                </Box>
                                <SpaceBetween direction="horizontal" size="xs">
                                  <Box variant="span">{item.average_rating.toFixed(2)}</Box>
                                  <StarsIcon stars={item.average_rating} />
                                </SpaceBetween>
                                <Box color="text-body-secondary">
                                  {item.review_count.toLocaleString()} reviews
                                </Box>
                              </SpaceBetween>
                            </div>
                          ),
                        },
                        {
                          id: 'response',
                          header: 'Response and coverage',
                          minWidth: 240,
                          cell: (item) => (
                            <SpaceBetween size="xxxs">
                              <Box>
                                <b>{formatMetricValue('response_rate', item.response_rate)}</b>{' '}
                                response rate
                              </Box>
                              <Box>
                                <b>{formatResponseTime(item.average_response_time_days)}</b>{' '}
                                response time
                              </Box>
                              <StatusIndicator
                                type={item.coverage_state === 'complete' ? 'success' : 'warning'}
                              >
                                {item.coverage_state === 'complete'
                                  ? 'Complete window'
                                  : 'Partial window'}
                              </StatusIndicator>
                            </SpaceBetween>
                          ),
                        },
                      ]}
                    />
                  </SpaceBetween>
                )}
              </Container>
            ) : null}
          </SpaceBetween>
        </Container>

        <Grid
          gridDefinition={[{ colspan: { default: 12, m: 6 } }, { colspan: { default: 12, m: 6 } }]}
        >
          <Container
            header={
              <Header
                variant="h2"
                description={`${windowDays}-day ${source === 'all' ? 'source-specific' : titleCase(source)} slice · ${formatDate(drilldownSnapshot?.as_of ?? history.latest_snapshot.as_of)}`}
              >
                Rating mix · pie
              </Header>
            }
          >
            <PieChart
              data={distribution}
              variant="donut"
              size="large"
              hideFilter
              innerMetricValue={currentMetric?.sample_size.toLocaleString() ?? '0'}
              innerMetricDescription="reviews"
              empty={<Box textAlign="center">No star distribution is available.</Box>}
            />
          </Container>
          <Container
            header={
              <Header
                variant="h2"
                description={`${windowDays}-day ${source === 'all' ? 'source-specific' : titleCase(source)} slice · ${formatDate(drilldownSnapshot?.as_of ?? history.latest_snapshot.as_of)}`}
              >
                Rating mix · bars
              </Header>
            }
          >
            <BarChart
              height={320}
              hideFilter
              xScaleType="categorical"
              series={[
                {
                  type: 'bar',
                  title: 'Reviews',
                  data: distribution.map((item) => ({ x: item.title, y: item.value })),
                },
              ]}
              xTitle="Star rating"
              yTitle="Reviews"
              empty={<Box textAlign="center">No star distribution is available.</Box>}
            />
          </Container>
        </Grid>

        <Container
          header={
            <Header
              variant="h2"
              description="Estimate how many new reviews at a chosen star rating would move the platform's current overall rating."
            >
              Rating target calculator
            </Header>
          }
        >
          <SpaceBetween size="l">
            {source === 'all' || !selectedSourceAvailability?.has_metrics ? (
              <Alert type="info" header="Choose one available source to calculate a rating target">
                Rating projections use one platform's observed lifetime rating and review count;
                they cannot combine source totals.
              </Alert>
            ) : null}
            <Grid
              gridDefinition={[
                { colspan: { default: 12, s: 4 } },
                { colspan: { default: 12, s: 3 } },
                { colspan: { default: 12, s: 3 } },
                { colspan: { default: 12, s: 2 } },
              ]}
            >
              <FormField label="Goal type">
                <SegmentedControl
                  selectedId={projectionMode}
                  options={[
                    { id: 'increase', text: 'Increase by' },
                    { id: 'target', text: 'Reach rating' },
                  ]}
                  onChange={({ detail }) => {
                    setProjectionMode(detail.selectedId);
                    setRatingGoal(detail.selectedId === 'increase' ? '0.1' : '4.6');
                  }}
                />
              </FormField>
              <FormField
                label={projectionMode === 'increase' ? 'Rating increase' : 'Target rating'}
              >
                <Input
                  type="number"
                  value={ratingGoal}
                  inputMode="decimal"
                  onChange={({ detail }) => setRatingGoal(detail.value)}
                />
              </FormField>
              <FormField label="New reviews are worth">
                <Select
                  selectedOption={{ value: incomingRating, label: `${incomingRating} stars` }}
                  options={[5, 4, 3, 2, 1].map((stars) => ({
                    value: String(stars),
                    label: `${stars} stars`,
                  }))}
                  onChange={({ detail }) => setIncomingRating(detail.selectedOption.value ?? '5')}
                />
              </FormField>
              <Box padding={{ top: 'l' }}>
                <Button
                  variant="primary"
                  fullWidth
                  loading={projectionMutation.isPending}
                  disabled={
                    source === 'all' ||
                    !selectedSourceAvailability?.has_metrics ||
                    !Number.isFinite(Number.parseFloat(ratingGoal))
                  }
                  onClick={() => projectionMutation.mutate()}
                >
                  Calculate
                </Button>
              </Box>
            </Grid>

            {projectionMutation.data ? (
              <Alert
                type={projectionMutation.data.reachable ? 'success' : 'warning'}
                header={
                  projectionMutation.data.reachable
                    ? projectionMutation.data.estimated_reviews_needed === 0
                      ? 'This rating goal is already met'
                      : `${projectionMutation.data.estimated_reviews_needed?.toLocaleString()} new ${projectionMutation.data.incoming_rating}-star reviews estimated`
                    : 'This target cannot be reached with the selected incoming rating'
                }
              >
                <KeyValuePairs
                  columns={3}
                  items={[
                    {
                      label: 'Current platform rating',
                      value: projectionMutation.data.current_rating.toFixed(2),
                    },
                    {
                      label: 'Current lifetime reviews',
                      value: projectionMutation.data.current_review_count.toLocaleString(),
                    },
                    {
                      label: 'Target rating',
                      value: projectionMutation.data.target_rating.toFixed(2),
                    },
                  ]}
                />
                <Box margin={{ top: 's' }} color="text-body-secondary">
                  {projectionMutation.data.warning}
                </Box>
              </Alert>
            ) : null}
            {projectionMutation.isError ? (
              <Alert type="error" header="The target could not be calculated">
                {projectionMutation.error.message}
              </Alert>
            ) : null}
          </SpaceBetween>
        </Container>

        <Alert type="info" header="How to read this history">
          Reconstructed points use reviews collected during the initial 545-day backfill. Observed
          points come from live 30-day tracking runs. Source availability and scoring versions are
          retained on every point so missing data is never presented as a zero score.
        </Alert>
      </SpaceBetween>
    </ViewLayout>
  );
}
